import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../Constants/modules';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import {
  deleteBillingReportApprovalHistory,
  getBillingReportBaseURL,
  updateBillingReportApproval,
} from '../../../Api/request/billingReport';
import { useBillingNotifFilters } from './billingReportUtils';
import darDeleteIcon from '../../../Assets/images/svg/dar_delete.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import './BillingReportApprovalHistory.scss';

const PAGE_SIZE = 10;
const PAGINATION_CONFIG = { page: 1, limit: PAGE_SIZE, rowsPerPageOptions: [] };

const buildBillingReportHistoryConfig = ({
  accessToken,
  baseURL,
  handleFilterUpdate,
  handleFilteredApiResponse,
  tableScrollHeight,
  transformPayload,
  userInfo,
}) => ({
  generalSettings: {
    enableApiFiltering: true,
    baseURL,
    showToolBar: true,
    showPagination: true,
    showTieredMenu: true,
    headers: { token: accessToken || '' },
    userInfo,
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: true },
      saveOrUpdateView: { endpoint: 'view', enabled: false },
      deleteView: { endpoint: 'view-delete', enabled: false },
      download: {
        endpoint: 'export',
        enabled: true,
        filename: () => 'Billing_Report_Approval_History',
        sheetName: () => 'Approval History',
      },
    },
    handler: {
      handleFilteredApiResponse,
      onFilterUpdate: handleFilterUpdate,
      transformPayload,
    },
    toolBarSettings: {
      showFilterOption: true,
      showColumnFilterMenu: true,
      showAddNew: false,
      showExpandCollapse: false,
      showDownloadExcel: true,
      downloadExcelSheetName: 'Approval History',
      showColumnCustomize: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showToolBarHeading: true,
      toolBarHeading: 'T&M Billing Report Approval History',
      showReset: false,
      showFilters: true,
    },
  },
  tableSettings: {
    size: 'small',
    pageSize: PAGE_SIZE,
    tableScrollHeight,
    loaderSkeleton: { rows: PAGE_SIZE, bodyCells: 2, columns: 7 },
    paginationConfig: PAGINATION_CONFIG,
    showExpandCollapse: false,
    maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 2 },
  },
});

const hasPermissionAction = (permissions = {}, moduleName = '', action = '') => {
  const moduleCandidates = [moduleName, moduleName.replace(/-/g, '_'), moduleName.replace(/_/g, '-')];
  return moduleCandidates.some((candidate) => (
    Array.isArray(permissions?.[candidate])
      && permissions[candidate].some((grantedAction) => (
        String(grantedAction).trim()
          .toLowerCase() === action.toLowerCase()
      ))
  ));
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  const dateLabel = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timeLabel = date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).toUpperCase();
  return `${dateLabel}, ${timeLabel}`;
};

const StatusBadge = ({ clickable, onClick, status }) => {
  if (!status) return null;
  const statusClass = `${status}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const BadgeElement = clickable ? 'button' : 'span';
  return (
    <BadgeElement
      className={[
        'shift-allowance-approval-history__status',
        `shift-allowance-approval-history__status--${statusClass}`,
        clickable ? 'shift-allowance-approval-history__status--clickable' : '',
      ].filter(Boolean).join(' ')}
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
    >
      {status}
    </BadgeElement>
  );
};

StatusBadge.propTypes = {
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  status: PropTypes.string,
};

StatusBadge.defaultProps = {
  clickable: false,
  onClick: () => {},
  status: '',
};

const DetailCell = ({ date, name, note, onStatusClick, status }) => {
  const canUpdate = String(status || '').toLowerCase() === 'open' && Boolean(onStatusClick);
  return (
    <div className="shift-allowance-approval-history__detail billing-report-approval-history__detail">
      {status ? (
        <StatusBadge clickable={canUpdate} onClick={onStatusClick} status={status} />
      ) : null}
      {name ? <span className="shift-allowance-approval-history__name">{name}</span> : null}
      {date ? <span className="shift-allowance-approval-history__date">{date}</span> : null}
      {note ? (
        <span className="shift-allowance-approval-history__note" title={note}>
          {note}
        </span>
      ) : null}
    </div>
  );
};

DetailCell.propTypes = {
  date: PropTypes.string,
  name: PropTypes.string,
  note: PropTypes.string,
  onStatusClick: PropTypes.func,
  status: PropTypes.string,
};

DetailCell.defaultProps = {
  date: '',
  name: '',
  note: '',
  onStatusClick: null,
  status: '',
};

const ApprovalStatusDialog = ({ approvalStage, dialog, onClose, onFieldChange, onSubmit }) => (
  <div
    className="shift-allowance-approval-history__status-backdrop"
    onClick={(event) => {
      if (!dialog.submitting && event.target === event.currentTarget) onClose();
    }}
    role="presentation"
  >
    <form
      className={[
        'shift-allowance-approval-history__status-modal',
        approvalStage === 2 ? 'billing-report-approval-history__status-modal--compact' : '',
      ].filter(Boolean).join(' ')}
      onSubmit={onSubmit}
    >
      <div className="shift-allowance-approval-history__status-modal-header">
        {`Update ${approvalStage === 2 ? 'client approval' : 'PM approval'} - ${
          dialog.row?.billingReportId || ''
        }`}
      </div>
      <div className="shift-allowance-approval-history__status-modal-body">
        <label
          className="shift-allowance-approval-history__status-modal-label"
          htmlFor="billing-report-approval-status"
        >
          Status*
        </label>
        <div className="shift-allowance-approval-history__status-modal-select-wrap">
          <select
            className="shift-allowance-approval-history__status-modal-select"
            disabled={dialog.submitting}
            id="billing-report-approval-status"
            onChange={(event) => onFieldChange('status', event.target.value)}
            value={dialog.status}
          >
            <option disabled value="">Select status</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
        {approvalStage === 1 ? <label
          className="shift-allowance-approval-history__status-modal-label"
          htmlFor="billing-report-approval-comments"
        >
          Comments*
        </label> : null}
        {approvalStage === 1 ? <textarea
          className="shift-allowance-approval-history__status-modal-input"
          disabled={dialog.submitting}
          id="billing-report-approval-comments"
          onChange={(event) => onFieldChange('comments', event.target.value)}
          placeholder="Enter..."
          rows={4}
          value={dialog.comments}
        /> : null}
      </div>
      <div className="shift-allowance-approval-history__status-modal-footer">
        <button
          className={[
            'shift-allowance-approval-history__status-modal-btn',
            'shift-allowance-approval-history__status-modal-btn-ghost',
          ].join(' ')}
          disabled={dialog.submitting}
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
        <button
          className={[
            'shift-allowance-approval-history__status-modal-btn',
            'shift-allowance-approval-history__status-modal-btn-primary',
          ].join(' ')}
          disabled={dialog.submitting}
          type="submit"
        >
          {dialog.submitting ? 'Updating...' : 'Update'}
        </button>
      </div>
    </form>
  </div>
);

ApprovalStatusDialog.propTypes = {
  approvalStage: PropTypes.number,
  dialog: PropTypes.shape({
    comments: PropTypes.string,
    row: PropTypes.object,
    status: PropTypes.string,
    submitting: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const BillingReportHistoryAction = ({ row, disabled, canDelete, onDelete }) => (canDelete ? (
  <div className="billing-report-approval-history__action-cell" onClick={(event) => event.stopPropagation()}>
    <button
      type="button"
      className="billing-report-approval-history__action-button"
      aria-label={`Delete approval history ${row.billingReportId || ''}`}
      disabled={disabled || ['approved', 'cancelled'].includes(String(row.status || '').toLowerCase())}
      onClick={() => onDelete(row)}
      title={String(row.status || '').toLowerCase() === 'approved'
        ? 'Approved requests cannot be cancelled'
        : String(row.status || '').toLowerCase() === 'cancelled'
          ? 'This request is already cancelled'
          : 'Cancel request'}
    >
      <img src={darDeleteIcon} alt="" aria-hidden="true" />
    </button>
  </div>
) : null);

BillingReportHistoryAction.propTypes = {
  disabled: PropTypes.bool,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
};

BillingReportHistoryAction.defaultProps = {
  disabled: false,
  canDelete: false,
};

const mapBillingApprovalResponse = (
  response,
  { deletingId, canUpdate, canDelete, openCancelDialog, openStatusDialog },
) => {
  normalizeTableUtilityResponse(response);
  const tableData = response?.data?.tableData;
  if (!Array.isArray(tableData)) return response;

  const displayRows = tableData.map((row) => ({
    ...row,
    projectName: (
      <span
        className="billing-report-approval-history__project-name"
        title={row.projectName || ''}
      >
        {row.projectName || ''}
      </span>
    ),
    requesterDetails: (
      <DetailCell
        date={formatDateTime(row.requestedAt)}
        name={[row.requesterId, row.requesterName].filter(Boolean).join(' - ')}
      />
    ),
    approvalDetails: (
      <DetailCell
        date={formatDateTime(row.actionAt || row.requestedAt)}
        name={[row.approverId, row.approverName].filter(Boolean).join(' - ')}
        note={row.comments || ''}
        onStatusClick={canUpdate && String(row.status || '').toLowerCase() === 'open'
          && !row.actionAt ? () => openStatusDialog(row, 1) : undefined}
        status={row.actionAt && ['open', 'approved'].includes(String(row.status || '').toLowerCase())
          ? 'Approved' : row.status}
      />
    ),
    clientApprovalDetails: (
      <DetailCell
        date={formatDateTime(row.approver2ActionAt)}
        name={[row.approver2Id, row.approver2Name].filter(Boolean).join(' - ')}
        onStatusClick={canUpdate && row.actionAt
          && String(row.approver2Status || '').toLowerCase() === 'open'
          ? () => openStatusDialog(row, 2) : undefined}
        status={row.approver2Status}
      />
    ),
    status: <StatusBadge status={row.status} />,
    action: (
      <BillingReportHistoryAction
        disabled={deletingId === Number(row.id)}
        canDelete={canDelete}
        onDelete={openCancelDialog}
        row={row}
      />
    ),
  }));

  response.data.tableData = displayRows;
  if (response.data.data && typeof response.data.data === 'object') {
    response.data.data.tableData = displayRows;
  }
  response.tableData = displayRows;
  return response;
};

const CancelBillingReportDialog = ({ open, submitting, onClose, onConfirm }) => {
  if (!open) return null;
  return (
    <div className="billing-report-cancel-dialog__overlay" role="presentation">
      <div
        className="billing-report-cancel-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-report-cancel-dialog-title"
      >
        <h2 id="billing-report-cancel-dialog-title">Delete Record</h2>
        <div className="billing-report-cancel-dialog__message">
          <img src={alertIcon} alt="" aria-hidden="true" />
          <p>Are you sure you want to delete this request?</p>
        </div>
        <div className="billing-report-cancel-dialog__actions">
          <button type="button" disabled={submitting} onClick={onClose}>Cancel</button>
          <button type="button" disabled={submitting} onClick={onConfirm}>
            {submitting ? 'Cancelling...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

CancelBillingReportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

CancelBillingReportDialog.defaultProps = {
  submitting: false,
};

const BillingReportApprovalHistory = () => {
  const { userProfile } = useAuth();
  const permissions = useMemo(() => {
    const userPermissions = userProfile?.permissions || {};
    const moduleName = MODULE_NAMES.MIS_BILLING_REPORT_APPROVAL_HISTORY;
    return {
      canUpdate: hasPermissionAction(userPermissions, moduleName, 'update'),
      canDelete: hasPermissionAction(userPermissions, moduleName, 'delete'),
    };
  }, [userProfile?.permissions]);
  const [tableScrollHeight, setTableScrollHeight] = useState('420px');
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ row: null, visible: false });
  const [statusDialog, setStatusDialog] = useState({
    comments: '',
    row: null,
    status: '',
    submitting: false,
    visible: false,
  });
  const deleteLockRef = useRef(false);
  const {
    handleApprovalUpdated,
    handleFilterUpdate,
    transformPayload,
  } = useBillingNotifFilters();
  const baseURL = `${getBillingReportBaseURL()}/history`;
  const tableKey = `billing-report-approval-history-${userProfile?.access_token || 'anonymous'}-${refreshKey}`;

  const userInfo = useMemo(() => ({
    id: userProfile?.id || '',
    emailId: userProfile?.email || '',
    permissions: userProfile?.permissions || {},
    roleDetails: userProfile?.roleDetails || [],
    employeeId: userProfile?.employeeId || '',
    globalColors: userProfile?.siteConfiguration?.global_colors || {},
  }), [userProfile]);

  const openCancelDialog = useCallback((row) => {
    if (!permissions.canDelete) {
      toast.error('You do not have permission to cancel billing report requests.');
      return;
    }
    const status = String(row?.status || '').toLowerCase();
    if (status === 'approved' || status === 'cancelled') return;
    setCancelDialog({ row, visible: true });
  }, [permissions.canDelete]);

  const closeCancelDialog = useCallback(() => {
    if (deleteLockRef.current) return;
    setCancelDialog({ row: null, visible: false });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!permissions.canDelete) {
      toast.error('You do not have permission to cancel billing report requests.');
      return;
    }
    const row = cancelDialog.row;
    const historyId = Number(row?.id);
    if (!Number.isInteger(historyId) || historyId < 1 || deleteLockRef.current) return;

    deleteLockRef.current = true;
    setDeletingId(historyId);
    try {
      const response = await deleteBillingReportApprovalHistory(historyId);
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to cancel the request.');
      }
      setCancelDialog({ row: null, visible: false });
      toast.success(response.data.message || 'Billing Report request cancelled.');
      setRefreshKey((current) => current + 1);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Unable to cancel the request.');
    } finally {
      deleteLockRef.current = false;
      setDeletingId(null);
    }
  }, [cancelDialog.row, permissions.canDelete]);

  const openStatusDialog = useCallback((row, approvalStage = 1) => {
    if (!permissions.canUpdate) {
      toast.error('You do not have permission to update billing report approval status.');
      return;
    }
    setStatusDialog({
      comments: '',
      approvalStage,
      row,
      status: '',
      submitting: false,
      visible: true,
    });
  }, [permissions.canUpdate]);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog({
      comments: '',
      row: null,
      status: '',
      submitting: false,
      visible: false,
    });
  }, []);

  const handleDialogFieldChange = useCallback((field, value) => {
    setStatusDialog((current) => ({ ...current, [field]: value }));
  }, []);

  const handleStatusSave = useCallback(async (event) => {
    event?.preventDefault();
    if (!permissions.canUpdate) {
      toast.error('You do not have permission to update billing report approval status.');
      return;
    }
    if (!statusDialog.status) {
      toast.error('Please select status.');
      return;
    }
    if (statusDialog.approvalStage === 1 && !statusDialog.comments.trim()) {
      toast.error('Please enter comment.');
      return;
    }
    const historyId = Number(statusDialog.row?.id);
    if (!Number.isInteger(historyId) || historyId < 1) {
      toast.error('Billing Report approval history id is missing.');
      return;
    }

    setStatusDialog((current) => ({ ...current, submitting: true }));
    try {
      const response = await updateBillingReportApproval(historyId, {
        status: statusDialog.status,
        comments: statusDialog.comments.trim(),
        approvalStage: statusDialog.approvalStage,
      });
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to update approval status.');
      }
      toast.success(response.data.message || 'Billing Report approval updated successfully.');
      handleApprovalUpdated();
      closeStatusDialog();
      setRefreshKey((current) => current + 1);
    } catch (error) {
      toast.error(
        error?.response?.data?.message
        || error?.message
        || 'Unable to update approval status.',
      );
    } finally {
      setStatusDialog((current) => ({ ...current, submitting: false }));
    }
  }, [
    closeStatusDialog,
    handleApprovalUpdated,
    permissions.canUpdate,
    statusDialog,
  ]);

  const handleFilteredApiResponse = useCallback((response) => (
    mapBillingApprovalResponse(response, {
      deletingId,
      canUpdate: permissions.canUpdate,
      canDelete: permissions.canDelete,
      openCancelDialog,
      openStatusDialog,
    })
  ), [
    deletingId,
    openCancelDialog,
    openStatusDialog,
    permissions.canDelete,
    permissions.canUpdate,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.max(Math.floor(window.innerHeight - 285), 260)}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const configuration = useMemo(() => buildBillingReportHistoryConfig({
    accessToken: userProfile?.access_token,
    baseURL,
    handleFilterUpdate,
    handleFilteredApiResponse,
    tableScrollHeight,
    transformPayload,
    userInfo,
  }), [
    baseURL,
    handleFilterUpdate,
    handleFilteredApiResponse,
    tableScrollHeight,
    transformPayload,
    userInfo,
    userProfile?.access_token,
  ]);

  return (
    <div className="shift-allowance-approval-history billing-report-approval-history">
      <TableUtility key={tableKey} configuration={configuration} showFilterOption />
      {statusDialog.visible ? (
        <ApprovalStatusDialog
          approvalStage={statusDialog.approvalStage}
          dialog={statusDialog}
          onClose={closeStatusDialog}
          onFieldChange={handleDialogFieldChange}
          onSubmit={handleStatusSave}
        />
      ) : null}
      <CancelBillingReportDialog
        open={cancelDialog.visible}
        submitting={deletingId === Number(cancelDialog.row?.id)}
        onClose={closeCancelDialog}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default BillingReportApprovalHistory;
